// apps/merchant-v3/src/lib/api/client.ts
//
// Phase 6 Wave 1 Task 03 — Canonical fetch wrapper for the merchant
// portal SPA. Wraps `window.fetch` to enforce:
//
//   1. `credentials: 'include'`  → JWT pw_token HTTP-only cookie
//      (cookie domain `.v3.printway.work` per F-05) is sent on every
//      request without the FE ever reading it directly.
//   2. CSRF double-submit — server sets `XSRF-TOKEN` (NOT HttpOnly) on
//      first response; client re-attaches it as `X-XSRF-TOKEN` header
//      on every state-changing verb (POST/PUT/PATCH/DELETE).
//   3. `X-Request-Id` header (UUID v4) for log correlation across the
//      stack (INTERFACE-STANDARDS.md correlation_id_header).
//   4. Optional `Idempotency-Key` helper for safe retry on
//      mutations — callers pass `{ idempotencyKey }` in opts.
//   5. Response envelope unwrap — success `{ ok:true, data }` returns
//      `data` directly; error `{ ok:false, error }` is thrown as
//      `ApiError` carrying the canonical error envelope so callers
//      can apply the INTERFACE-STANDARDS message-priority rule
//      (`user_message > message > network_fallback`).
//   6. 401 → single-flight refresh (POST /auth/refresh). Concurrent
//      401s queue and replay after the single refresh resolves
//      . On refresh failure the queue is rejected and the
//      AuthProvider redirects to `/login?returnUrl=…`.
//
// Phase 6.1 Wave 5 Task 23 — scopeVersion observer. resolveStoreScope
// (task-19, BE) stamps an `x-scope-version` header on every scoped read.
// `request()` forwards the parsed value to any registered observer so the
// FE interceptor (ScopeVersionProvider) can detect drift → invalidate the
// TanStack cache + non-blocking toast (P6.1.D-21). Purely additive: the
// header is advisory FE metadata, never a gate.
//
// vg-binding: PLAN:task-03
// vg-binding: PLAN:task-23
// vg-binding: P6.D-02
// vg-binding: P6.D-172
// vg-binding: P6.D-205
// vg-binding: P6.1.D-21
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:response-envelope

/* eslint-disable @typescript-eslint/no-explicit-any */

import { i18n } from '@/lib/i18n';

// --- types ----------------------------------------------------------------

/** Canonical error envelope per INTERFACE-STANDARDS.md §error-shape. */
export interface ApiErrorEnvelope {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    details?: Record<string, unknown>;
    field_errors?: Record<string, string[]>;
    request_id?: string;
  };
}

/** Canonical success envelope per INTERFACE-STANDARDS.md §response-envelope. */
export interface ApiSuccessEnvelope<T> {
  ok: true;
  data: T;
  message?: string;
  meta?: Record<string, unknown>;
  request_id?: string;
}

export type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiErrorEnvelope;

/** Thrown by the request helpers so callers can branch on `error.code`. */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly userMessage?: string;
  public readonly details?: Record<string, unknown>;
  public readonly fieldErrors?: Record<string, string[]>;
  public readonly requestId?: string;

  constructor(status: number, env: ApiErrorEnvelope) {
    // INTERFACE-STANDARDS message priority: user_message > message > fallback
    super(env.error.user_message ?? env.error.message ?? 'Request failed');
    this.name = 'ApiError';
    this.code = env.error.code;
    this.status = status;
    this.userMessage = env.error.user_message;
    this.details = env.error.details;
    this.fieldErrors = env.error.field_errors;
    this.requestId = env.error.request_id;
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  /** Bypass auto-refresh on 401 (used by /auth/refresh itself). */
  skipAuthRefresh?: boolean;
  /** Bypass envelope unwrap; return raw Response (used for binary downloads). */
  raw?: boolean;
  /** Return the full success envelope ({ok,data,meta,…}) instead of just
   *  `data`. Use when the caller needs `meta` (e.g. cursor pagination). */
  keepEnvelope?: boolean;
  /** Optional idempotency key — server-side request dedupe. */
  idempotencyKey?: string;
  /** AbortSignal forwarded to fetch. */
  signal?: AbortSignal;
}

// --- module-local state ---------------------------------------------------

const ENV_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined;

/** Default to relative `/api/v1` so dev proxy + same-origin both work; FE
 *  reads `VITE_API_BASE_URL` when targeting the canonical apex
 *  (`https://api-v3.printway.work` per F-05). */
export const API_BASE_URL: string = (ENV_BASE ?? '').replace(/\/$/, '');

/** CSRF token cookie name (server emits during /auth/login). */
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';

/** Network fallback message — INTERFACE-STANDARDS priority §3 last resort.
 *  Lazy getter so the copy follows the active locale at call time. */
function networkFallbackMessage(): string {
  return i18n.t('auth.errors.network', {
    defaultValue: 'Cannot reach the server. Please try again.',
  });
}

/** Single-flight refresh state. Concurrent 401s share one in-flight call. */
let refreshInFlight: Promise<boolean> | null = null;

/** Hook fired when /auth/refresh fails — AuthProvider attaches a redirector. */
type UnauthorizedHandler = (returnUrl: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(h: UnauthorizedHandler | null): void {
  unauthorizedHandler = h;
}

/**
 * P6.1.D-21 — scopeVersion drift observer. resolveStoreScope (BE task-19)
 * stamps `x-scope-version` on scoped reads; `request()` parses it and hands
 * the integer to this observer (when registered) so the FE interceptor can
 * compare against the last-seen version and refetch on drift. A missing /
 * unparsable header is skipped (observer not called). Best-effort: never
 * throws into the request path.
 */
export const SCOPE_VERSION_HEADER = 'x-scope-version';
type ScopeVersionObserver = (version: number) => void;
let scopeVersionObserver: ScopeVersionObserver | null = null;

export function setScopeVersionObserver(
  o: ScopeVersionObserver | null,
): void {
  scopeVersionObserver = o;
}

function notifyScopeVersion(res: Response): void {
  if (!scopeVersionObserver) return;
  try {
    const raw = res.headers.get(SCOPE_VERSION_HEADER);
    if (raw == null) return;
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed)) {
      scopeVersionObserver(parsed);
    }
  } catch {
    /* header read/observer failure must never break the request path */
  }
}

/**
 * UAT #2658 — global maintenance gate. When the BE returns 503
 * MAINTENANCE_MODE_ACTIVE, stash the operator's message and hard-redirect to
 * the full-screen /maintenance page (unless already there, to avoid a loop).
 * Hard nav (not react-router) keeps this dependency-free from the client layer.
 */
const MAINTENANCE_MESSAGE_KEY = 'pw_maintenance_msg';
function redirectToMaintenance(message: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/maintenance') return;
  try {
    if (message && message.trim().length > 0) {
      window.sessionStorage.setItem(MAINTENANCE_MESSAGE_KEY, message);
    }
  } catch {
    /* sessionStorage unavailable — still redirect */
  }
  window.location.assign('/maintenance');
}

// --- helpers --------------------------------------------------------------

/** Read non-HttpOnly cookie value by name (CSRF double-submit pattern). */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + escaped + '=([^;]*)'),
  );
  return match && typeof match[1] === 'string' ? decodeURIComponent(match[1]) : null;
}

function readCsrfToken(): string | null {
  return readCookie(CSRF_COOKIE_NAME) ?? readCookie('csrf_token');
}

/** Public helper for raw-fetch callers (multipart upload, blob download)
 *  that bypass apiClient and need to manually attach the CSRF header on
 *  mutating requests (double-submit). */
export function getCsrfHeaderValue(): string | null {
  return readCsrfToken();
}

/** RFC 4122 v4 UUID — used as X-Request-Id for log correlation. */
function newRequestId(): string {
  // Use crypto.randomUUID when available; fallback for non-secure contexts.
  const c = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'req_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isMutation(method: HttpMethod): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH' ||
    method === 'DELETE';
}

/** Single-flight 401 refresh. Multiple concurrent 401s collapse to ONE
 *  POST /auth/refresh; queued promises all resolve to the same boolean. */
async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const url = API_BASE_URL + '/api/v1/auth/refresh';
      const csrf = readCsrfToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-Id': newRequestId(),
      };
      if (csrf) {
        headers['X-XSRF-TOKEN'] = csrf;
      }
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({}),
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Clear the slot AFTER queue drains — slight delay so very-near-
      // simultaneous calls still piggy-back. setTimeout 0 is enough.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

/** Build the absolute URL — caller passes either a full URL or a relative
 *  path beginning with `/`. */
function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!path.startsWith('/')) path = '/' + path;
  return API_BASE_URL + path;
}

// --- core request fn ------------------------------------------------------

/** Lower-level request; handles envelopes, refresh retry, network fallback. */
export async function request<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const method: HttpMethod = opts.method ?? 'GET';
  const url = buildUrl(path);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': newRequestId(),
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] ??= 'application/json';
  }
  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }
  if (isMutation(method)) {
    const csrf = readCsrfToken();
    if (csrf) {
      headers['X-XSRF-TOKEN'] = csrf;
    }
  }

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.body =
      opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (networkErr) {
    // Network/CORS/abort — no body. Synthesize an envelope so callers
    // get a consistent shape (INTERFACE-STANDARDS network_fallback).
    throw new ApiError(0, {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: networkFallbackMessage(),
        details: { reason: String(networkErr) },
      },
    });
  }

  // P6.1.D-21 — forward the live scope version (if the BE stamped one) to
  // the registered observer so the FE interceptor detects drift. Fires on
  // every response (success or error envelope) before any branch.
  notifyScopeVersion(res);

  // 401 single-flight refresh + retry.
  if (res.status === 401 && !opts.skipAuthRefresh) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      // Replay original request once. Re-read CSRF cookie in case
      // /auth/refresh rotated it.
      return request<T>(path, { ...opts, skipAuthRefresh: true });
    }
    // Refresh failed. Notify AuthProvider so it can redirect to /login.
    if (unauthorizedHandler) {
      const returnUrl =
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : '/';
      unauthorizedHandler(returnUrl);
    }
    // Fall through — return the 401 envelope to the caller.
  }

  if (opts.raw) return res as unknown as T;

  // Body-less success (204).
  if (res.status === 204) return null as unknown as T;

  let body: ApiEnvelope<T>;
  try {
    body = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(res.status, {
      ok: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: networkFallbackMessage(),
      },
    });
  }

  const rawBody = body as any;

  if (!res.ok || (body && body.ok === false) || rawBody?.success === false) {
    const env: ApiErrorEnvelope =
      body && body.ok === false
        ? body
        : rawBody?.success === false && rawBody.error
          ? {
              ok: false,
              error: {
                code: String(rawBody.error.code ?? 'HTTP_' + res.status),
                message: String(
                  rawBody.error.user_message ??
                    rawBody.error.message ??
                    networkFallbackMessage(),
                ),
                user_message: rawBody.error.user_message,
                details: rawBody.error.details,
                field_errors: rawBody.error.field_errors,
                request_id: rawBody.error.request_id ?? rawBody.request_id ??
                  rawBody.requestId,
              },
            }
        : {
            ok: false,
            error: {
              code: 'HTTP_' + res.status,
              message: networkFallbackMessage(),
            },
          };
    // UAT #2658 — intercept the platform maintenance gate before surfacing the
    // error to callers, so the whole SPA drops to the maintenance page.
    if (res.status === 503 && env.error.code === 'MAINTENANCE_MODE_ACTIVE') {
      redirectToMaintenance(env.error.user_message ?? env.error.message);
    }
    throw new ApiError(res.status, env);
  }

  if (rawBody?.success === true && 'data' in rawBody) {
    if (opts.keepEnvelope) {
      return rawBody as unknown as T;
    }
    return rawBody.data as T;
  }

  if (opts.keepEnvelope) {
    return body as unknown as T;
  }
  return (body as ApiSuccessEnvelope<T>).data;
}

// --- public verb helpers --------------------------------------------------

export const apiClient = {
  get<T = unknown>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) {
    return request<T>(path, { ...opts, method: 'GET' });
  },
  /**
   * Like `get` but returns the full success envelope so the caller can
   * read `meta` (e.g. cursor pagination). Throws `ApiError` on error
   * envelopes exactly like `get`.
   */
  getEnvelope<T = unknown>(
    path: string,
    opts?: Omit<RequestOptions, 'method' | 'body' | 'keepEnvelope'>,
  ): Promise<ApiSuccessEnvelope<T>> {
    return request<ApiSuccessEnvelope<T>>(path, {
      ...opts,
      method: 'GET',
      keepEnvelope: true,
    });
  },
  post<T = unknown>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) {
    return request<T>(path, { ...opts, method: 'POST', body });
  },
  put<T = unknown>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) {
    return request<T>(path, { ...opts, method: 'PUT', body });
  },
  patch<T = unknown>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) {
    return request<T>(path, { ...opts, method: 'PATCH', body });
  },
  delete<T = unknown>(
    path: string,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ) {
    return request<T>(path, { ...opts, method: 'DELETE' });
  },
} as const;
